import { computed } from 'vue'
import { useAuth } from '@/composables/useAuth'

export interface FacilityGate {
	deploymentType?: number
	allowSSOAdminUserCreation?: boolean
	isAdmin?: boolean
}

export interface FacilityGateOpts {
	isTurnCareAdmin: boolean
}

/**
 * SSO-aware user-creation gating, mirroring the legacy portal's `facilityMixin`.
 * Deployment types: 1 = Off, 2 = Evaluation, 3 = Customer no-SSO, 4 = Customer SSO.
 *
 * Pure functions (no store/router access) so they can be unit-tested directly,
 * matching the `opts`-argument style of `computePermissions` (useAuth) and
 * `getRoleOptionsForUser` (Users.logic).
 */

/** deploymentType === 4 (legacy `facilityMixin.isSSOFacility`). */
export function isSSOFacility(facility?: FacilityGate | null): boolean {
	return facility?.deploymentType === 4
}

/**
 * Whether users may be created for the given facility (legacy `facilityMixin.canCreateUsers`):
 * - TurnCareAdmin: always (they were never blocked from creating the row).
 * - Evaluation (2) / Customer no-SSO (3): allowed.
 * - SSO (4): only when `allowSSOAdminUserCreation` is set.
 * - otherwise: denied.
 *
 * Fail-safe: a null/undefined facility for a non-TCA caller returns `true` so a
 * missing session facility record never hides creation the server would allow
 * (the server activation step in tenant-user-setup remains the authority).
 */
export function canCreateUsers(facility: FacilityGate | null | undefined, opts: FacilityGateOpts): boolean {
	if (opts.isTurnCareAdmin) return true
	if (!facility) return true
	if (facility.deploymentType === 2 || facility.deploymentType === 3) return true
	if (isSSOFacility(facility) && !!facility.allowSSOAdminUserCreation) return true
	return false
}

/**
 * Whether a simulation user may be created (legacy `UserList` `allowSimulationUser`):
 * TurnCareAdmin view only, for non-admin SSO facilities.
 */
export function canCreateSimulationUser(facility: FacilityGate | null | undefined, opts: FacilityGateOpts): boolean {
	if (!opts.isTurnCareAdmin) return false
	if (!facility) return false
	return !facility.isAdmin && isSSOFacility(facility)
}

export interface DeletableUser {
	scimId?: string | null
	simulationUser?: boolean | null
}

export interface DeleteUserGateOpts extends FacilityGateOpts {
	/** Caller holds the delete capability (TurnCareAdmin or CustomerAdmin). */
	canDeleteUsers: boolean
	/** The row belongs to the signed-in user. */
	isCurrentUser: boolean
}

/**
 * Whether the delete action may be offered for a user row (legacy `UserList.canDeleteItem`):
 * - the caller must hold the delete capability, and may never delete themselves;
 * - a SCIM-provisioned user is never deletable — the IdP owns that lifecycle;
 * - wherever users may be created (eval 2 / no-SSO 3 / TCA / allowSSOAdminUserCreation): allowed;
 * - at an SSO facility otherwise: simulation users only.
 *
 * The SCIM guard exists in gen1 but is commented out (`UserList.ts:673`), which left a
 * TurnCareAdmin able to delete one; its stated intent is honoured here instead.
 *
 * Inherits `canCreateUsers`'s missing-facility fail-safe, reachable only by a non-TCA whose
 * token carries no facility. Gating is presentational — there is no server-side guard, in
 * either generation.
 */
export function canDeleteUser(
	user: DeletableUser | null | undefined,
	facility: FacilityGate | null | undefined,
	opts: DeleteUserGateOpts,
): boolean {
	if (!opts.canDeleteUsers) return false
	if (user?.scimId) return false
	if (opts.isCurrentUser) return false
	if (canCreateUsers(facility, opts)) return true
	if (isSSOFacility(facility)) return !!user?.simulationUser
	return false
}

/** A user row as the login actions on the edit form see it. */
export interface LoginActionUser {
	id?: string | null
	/** Present ⇒ the IdP owns this account, not the portal. */
	scimId?: string | null
	/** Present ⇒ a Cognito account already exists (gen1's `hasLoginAccess`). */
	cognitoUserId?: string | null
	simulationUser?: boolean | null
}

/**
 * Whether "Setup User" may be offered — gen1's `canActivate` (ModalEditUser.ts).
 *
 * Only for an existing row, never for a SCIM-provisioned user (the IdP creates their login on
 * first sign-in), and only where users may be created at all — with simulation users exempt,
 * since they exist precisely to be given a login at an SSO facility that otherwise forbids it.
 */
export function canActivateUser(
	user: LoginActionUser | null | undefined,
	facility: FacilityGate | null | undefined,
	opts: FacilityGateOpts,
): boolean {
	if (!user?.id) return false
	if (user.scimId) return false
	if (user.cognitoUserId) return false
	return canCreateUsers(facility, opts) || !!user.simulationUser
}

/**
 * Whether "Reset Password" may be offered — gen1's `hasLoginAccess && canEditPersonalInfo`
 * (ModalEditUser.vue:92). A SCIM user authenticates against the IdP, so resetting their Cognito
 * password only emails a temporary one they can never use.
 */
export function canResetUserPassword(user: LoginActionUser | null | undefined): boolean {
	if (!user?.cognitoUserId) return false
	return !user.scimId
}

/**
 * Whether the personal-info fields are editable — gen1's `canEditPersonalInfo`. A SCIM record is
 * overwritten from the directory on the next sync, so letting an admin edit it here only
 * manufactures a disagreement.
 */
export function canEditPersonalInfo(user: LoginActionUser | null | undefined): boolean {
	return !user?.scimId
}

export interface AgreementUser {
	isAdmin?: boolean | null
	simulationUser?: boolean | null
}

export interface AgreementFacility {
	agreementSigned?: boolean | null
	isEvaluation?: boolean | null
}

/**
 * Whether the licence-agreement modal should be raised — gen1 `App.ts` `checkAgreement`:
 * `!facility.agreementSigned && !user.simulationUser && (user.isAdmin || facility.isEvaluation)`.
 *
 * The two narrowing clauses are the point. A simulation account has no authority to accept terms
 * for the facility, and only an admin can sign (AgreementModal offers everyone else Logout alone)
 * — so prompting anyone outside those cases traps them behind a modal they cannot clear. The
 * evaluation arm is what still shows it to a non-admin at an evaluation facility, matching gen1.
 */
export function shouldShowAgreementModal(
	user: AgreementUser | null | undefined,
	facility: AgreementFacility | null | undefined,
): boolean {
	if (facility?.agreementSigned) return false
	if (user?.simulationUser) return false
	return !!user?.isAdmin || !!facility?.isEvaluation
}

/**
 * View-facing wrapper binding `isTurnCareAdmin` from `useAuth().permissions`,
 * mirroring how `useAuth()` wraps `computePermissions`.
 */
export function useFacility() {
	const { permissions } = useAuth()
	const isTurnCareAdmin = computed(() => permissions.value.isTurnCareAdmin)

	return {
		isSSOFacility,
		canCreateUsers: (facility: FacilityGate | null | undefined) =>
			canCreateUsers(facility, { isTurnCareAdmin: isTurnCareAdmin.value }),
		canCreateSimulationUser: (facility: FacilityGate | null | undefined) =>
			canCreateSimulationUser(facility, { isTurnCareAdmin: isTurnCareAdmin.value }),
		canDeleteUser: (
			user: DeletableUser | null | undefined,
			facility: FacilityGate | null | undefined,
			opts: { canDeleteUsers: boolean; isCurrentUser: boolean },
		) => canDeleteUser(user, facility, { ...opts, isTurnCareAdmin: isTurnCareAdmin.value }),
		canActivateUser: (
			user: LoginActionUser | null | undefined,
			facility: FacilityGate | null | undefined,
		) => canActivateUser(user, facility, { isTurnCareAdmin: isTurnCareAdmin.value }),
		canResetUserPassword,
		canEditPersonalInfo,
	}
}
